import {
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Injectable,
  Input,
  NgModule,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {Overlay, OverlayModule, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';

@Component({
  selector: 'sd-menu',
  exportAs: 'sdMenu',
  standalone: true,
  template: `<ng-template><div class="sd-menu min-w-[10rem] rounded-lg border border-[var(--m365-border-rest,#ececf0)] bg-white py-1 shadow-lg" role="menu"><ng-content></ng-content></div></ng-template>`,
})
export class SdMenu {
  @ViewChild(TemplateRef, {static: true}) template!: TemplateRef<unknown>;
  xPosition: 'before' | 'after' = 'after';
  yPosition: 'above' | 'below' = 'below';
  closed = new EventEmitter<void>();
}

@Directive({
  selector: '[sd-menu-item], [sdMenuItem]',
  standalone: true,
  host: {
    class: 'sd-menu-item flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/[0.04]',
    role: 'menuitem',
    type: 'button',
  },
})
export class SdMenuItem {}

@Directive({
  selector: '[sdMenuTriggerFor]',
  standalone: true,
})
export class SdMenuTrigger {
  @Input('sdMenuTriggerFor') menu!: SdMenu;
  @Output() menuOpened = new EventEmitter<void>();
  @Output() menuClosed = new EventEmitter<void>();
  private overlayRef: OverlayRef | null = null;

  constructor(
    private readonly overlay: Overlay,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly vcr: ViewContainerRef,
  ) {}

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  toggle(): void {
    this.overlayRef ? this.close() : this.open();
  }

  open(): void {
    if (this.overlayRef || !this.menu?.template) {
      return;
    }
    const position = this.overlay.position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4},
        {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4},
      ]);
    this.overlayRef = this.overlay.create({
      positionStrategy: position,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
    this.overlayRef.attach(new TemplatePortal(this.menu.template, this.vcr));
    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.menuOpened.emit();
  }

  close(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.menuClosed.emit();
    this.menu?.closed.emit();
  }
}

@Injectable({providedIn: 'root'})
export class SdMenuTriggerStub {}

@NgModule({
  imports: [OverlayModule, SdMenu, SdMenuItem, SdMenuTrigger],
  exports: [SdMenu, SdMenuItem, SdMenuTrigger],
})
export class SdMenuModule {}
